import { InfiniteTable } from '@/components/_common';
import DeleteIcon from '@/components/_common/DeleteIcon';
import EditIcon from '@/components/_common/EditIcon';
import StatusTag from '@/components/_common/StatusTag';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import { usePurchases } from '@/core/domain/purchase/Purchase.hooks';
import {
  PurchaseBoschXmlStatus,
  PurchaseBoschXmlStatusColor,
  PurchaseBoschXmlStatusLabel,
  PurchaseListParams,
  PurchaseListResponse,
} from '@/core/domain/purchase/Purchase.types';
import { useWindowSize } from '@/hooks';
import Authorities from '@/types/Authority';
import { MatrixPartnerOrigin } from '@/types/Register';
import { Status, StatusColor } from '@/utils/enum/statusEnum';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import formatterPrice from '@/utils/number/formatterPrice';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { COLUMNS_WIDTH, getTableScrollX } from '@/utils/table';
import { Divider, Popover, Space } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import React, { useMemo } from 'react';
import { CheckSquare, XSquare } from 'react-feather';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import PurchaseListActionDownloadXml from './ActionDownloadXml';

interface Props {
  filters?: PurchaseListParams;
  handleRemoveClick: (record: PurchaseListResponse) => void;
}
const PurchasesList: React.FC<Props> = ({ filters, handleRemoveClick }) => {
  const intl = useIntl();
  const { isMobile } = useWindowSize();

  const { data, isFetching, hasNextPage, fetchNextPage } =
    usePurchases(filters);

  const purchases = flatInfiniteData(data);

  const hasEditPurchasePermission = hasPermissions([
    Authorities.MOVIMENTATION_PURCHASE_EDIT,
    Authorities.MOVIMENTATION_PURCHASE_ITEM_EDIT,
  ]);

  const matrixDetail = useMatrix();

  const isBoschCarService = useMemo(() => {
    return (
      matrixDetail.data?.partnerOrigin === MatrixPartnerOrigin.BOSCH_CAR_SERVICE
    );
  }, [matrixDetail.data]);

  const columns: ColumnsType<PurchaseListResponse> = [
    {
      dataIndex: 'code',
      title: intl.formatMessage({ id: 'purchases.list.header.code' }),
      width: 100,
    },
    {
      dataIndex: 'company',
      title: intl.formatMessage({
        id: 'purchases.list.header.company',
      }),
      width: 200,
      ellipsis: true,
    },
    {
      dataIndex: 'supplier',
      title: intl.formatMessage({
        id: 'purchases.list.header.provider',
      }),
      width: 200,
      ellipsis: true,
    },
    {
      dataIndex: 'documentNumber',
      title: intl.formatMessage({
        id: 'purchases.list.header.documentNumber',
      }),
      width: 150,
    },
    {
      dataIndex: 'releaseDate',
      title: intl.formatMessage({
        id: 'purchases.list.header.releaseDate',
      }),
      width: 150,
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
    },
    {
      dataIndex: 'situation',
      title: intl.formatMessage({
        id: 'purchases.list.header.situation',
      }),
      width: 130,
      render: (value: Status) => {
        if (!value) {
          return '-';
        }
        return (
          <StatusTag
            color={StatusColor[value]}
            label={intl.formatMessage({ id: `directSale.${value}` })}
          />
        );
      },
    },
    isBoschCarService
      ? {
          dataIndex: 'boschXmlSendStatus',
          title: intl.formatMessage({
            id: 'purchases.list.header.boschStatus',
          }),
          width: 130,
          render: (status: PurchaseBoschXmlStatus) => {
            if (!status) {
              return '-';
            }
            return (
              <StatusTag
                color={PurchaseBoschXmlStatusColor[status]}
                label={PurchaseBoschXmlStatusLabel[status]}
              />
            );
          },
        }
      : {},
    {
      title: 'Status de Agrupamento',
      dataIndex: 'isGrouping',
      align: 'center',
      width: 150,
      render: (_, record) => (
        <Popover
          content={
            record.isGrouping ? 'Compra agrupada' : 'Compra não agrupada'
          }
        >
          {record.isGrouping ? (
            <Link
              to={`/app/movements/purchases/purchasesReleases/${record.id}/purchaseGroupings`}
              style={{
                pointerEvents: !hasEditPurchasePermission ? 'none' : undefined,
              }}
            >
              <CheckSquare color="green" size={20} />
            </Link>
          ) : (
            <XSquare color="red" size={20} />
          )}
        </Popover>
      ),
    },
    {
      dataIndex: 'totalAmount',
      title: 'Valor total',
      width: COLUMNS_WIDTH.price,
      render: (value) => (value ? formatterPrice(value) : '-'),
    },
    {
      key: 'actions',
      title: intl.formatMessage({ id: 'purchases.list.header.actions' }),
      render: (_, record) => (
        <Space>
          <PurchaseListActionDownloadXml
            purchaseId={record.id}
            purchaseCode={record.code}
            hasXml={!!record.xmlPath}
          />

          <Divider type="vertical" />

          <EditIcon
            redirect={`/app/movements/purchases/purchasesReleases/${record.id}`}
            disabled={!hasEditPurchasePermission}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleRemoveClick(record)}
            disabled={
              !hasPermissions([Authorities.MOVIMENTATION_PURCHASE_DELETE]) ||
              record.hasFinancial ||
              record.boschMigration ||
              record.isDeletionDisabled
            }
            title={record.hasFinancial && 'Excluir'}
            placement={record.hasFinancial && !isMobile ? 'right' : 'top'}
            content={
              record.hasFinancial &&
              'Não é possível excluir devido à existência de um registro financeiro associado.'
            }
          />
        </Space>
      ),
      width: 150,
      fixed: 'right',
      align: 'right',
    },
  ];
  return (
    <InfiniteTable<PurchaseListResponse>
      size="small"
      columns={columns.filter((column) => !!column.title)}
      dataSource={purchases}
      loading={isFetching}
      rowKey={(row) => row.id}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      scroll={{ x: getTableScrollX(columns) }}
    />
  );
};

export default PurchasesList;
